import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, GripVertical, Star, Trash2, ImagePlus, Loader2, Globe, Users, Lock, Check, Pencil, Images, ChevronDown, Archive, ShieldCheck, Share2, Crop } from "lucide-react";
import { ShareDialog } from "@/components/ShareDialog";
import { listingShareUrl } from "@/lib/share";
import { PageShell } from "@/components/Navbar";
import { ListingGallery } from "@/components/ListingGallery";
import { PROPERTY_MEDIA_CLASSES, MEDIA_CLASS_LABEL } from "@/components/AdaptiveMediaFrame";
import { CoverComposer } from "@/components/CoverComposer";
import { useAuth } from "@/context/AuthContext";
import { priceLabel } from "@/components/proptech";
import { saleTypeShortLabel } from "@/lib/taxonomy";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ListingCommercialDetails } from "@/components/ListingCommercialDetails";
import AdPlacement from "@/components/AdPlacement";

const OWNER_STATUSES = ["Active", "Under Negotiation", "Reserved", "Sold", "Leased", "Unavailable"];

const VISIBILITY_OPTIONS = [
  { key: "PUBLIC_MARKETPLACE", label: "Public Marketplace", desc: "Visible to anyone browsing PropertyConnect.", icon: Globe },
  { key: "NETWORK_ONLY", label: "Network Only", desc: "Visible only within PropertyConnect professional/network workflows.", icon: Users },
  { key: "PRIVATE", label: "Private", desc: "Not publicly visible.", icon: Lock },
];

export default function PropertyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const fileRef = useRef();
  const [p, setP] = useState(null);
  const [drag, setDrag] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [pendingVis, setPendingVis] = useState(null);
  const [savingVis, setSavingVis] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // { kind:'status'|'archive', status, title, body }
  const [shareOpen, setShareOpen] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const canShare = p?.visibility === "PUBLIC_MARKETPLACE" && p?.status === "Active";
  const openShare = () => canShare ? setShareOpen(true) : toast.info("Make this listing Public Marketplace to share it publicly.");

  const applyStatus = async (status) => {
    setStatusBusy(true);
    try {
      await api.patch(`/properties/${id}/status`, { status });
      toast.success(`Listing marked ${status}`);
      setP((prev) => ({ ...prev, status }));
    } catch (e) { toast.error(apiError(e)); }
    finally { setStatusBusy(false); }
  };
  const requestStatus = (status) => {
    if (status === "Sold" || status === "Leased") {
      setPendingAction({ kind: "status", status,
        title: `Mark this listing as ${status}?`,
        body: `Marking this listing as ${status} will remove it from active matching and marketplace discovery. Continue?` });
    } else { applyStatus(status); }
  };
  const requestArchive = () => setPendingAction({ kind: "archive",
    title: "Archive this listing?",
    body: "Archiving removes this listing from the marketplace and active matching. This is a soft-archive — the listing and its historical inquiries and collaborations are preserved and can be reviewed later. Continue?" });
  const confirmAction = async () => {
    const act = pendingAction; setPendingAction(null);
    if (!act) return;
    if (act.kind === "archive") {
      try { await api.delete(`/properties/${id}`); toast.success("Listing archived"); nav("/my-properties"); }
      catch (e) { toast.error(apiError(e)); }
    } else { applyStatus(act.status); }
  };
  const scrollToPhotos = () => {
    const el = document.getElementById("manage-photos-section");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const applyVisibility = async (v) => {
    setSavingVis(true);
    try {
      await api.patch(`/properties/${id}/visibility`, { visibility: v });
      setP((prev) => ({ ...prev, visibility: v }));
      toast.success("Visibility updated");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingVis(false); setPendingVis(null); }
  };
  const onSelectVisibility = (v, current) => {
    if (v === current || savingVis) return;
    if (v === "PUBLIC_MARKETPLACE") setPendingVis(v);
    else applyVisibility(v);
  };

  const load = () => api.get(`/properties/${id}`).then((r) => { setP(r.data); }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [id]);

  if (!p) return <PageShell testid="property-detail-page"><p className="text-slate-500">Loading…</p></PageShell>;
  const uh3Eligible = ["LICENSED_BROKER", "PROPERTY_MANAGER"].includes(user?.role)
    && user?.verification_status === "PROFESSIONAL_VERIFIED"
    && (user?.account_status || "ACTIVE") === "ACTIVE";
  const hostingRestricted = p.listing_type === "Unit Hosting" && !uh3Eligible;
  const photos = p.photo_urls?.length ? p.photo_urls : [{ id: "cover", url: p.image }];

  const persist = async (order, coverId, mediaClasses) => {
    try { const body = { order, cover_id: coverId }; if (mediaClasses) body.media_classes = mediaClasses; const { data } = await api.put(`/properties/${id}/photos`, body); setP((prev) => ({ ...prev, ...data })); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setMediaClass = (pid, cls) => persist((p.photo_urls || []).map((x) => x.id), p.cover_id, { [pid]: cls });
  const onDrop = (target) => {
    if (drag == null || drag === target) return;
    const ids = p.photo_urls.map((x) => x.id);
    const [m] = ids.splice(drag, 1); ids.splice(target, 0, m);
    setDrag(null); persist(ids, p.cover_id);
  };
  const makeCover = (pid) => persist(p.photo_urls.map((x) => x.id), pid);
  const coverPhoto = (p.photo_urls || []).find((x) => x.id === p.cover_id) || null;
  // V3 Marketplace Cover commit: optionally upload a dedicated cover (B) or pick a photo (A/C),
  // set it as the cover, then persist its 4:3 presentation. Source assets stay in the gallery.
  const commitCover = async ({ file, pickPhotoId, body }) => {
    try {
      let targetId = pickPhotoId;
      let order = (p.photo_urls || []).map((x) => x.id);
      if (file) {
        const fd = new FormData(); fd.append("files", file);
        const { data } = await api.post(`/properties/${id}/photos`, fd);
        order = data.photo_urls.map((x) => x.id);
        targetId = order[order.length - 1];
        setP((prev) => ({ ...prev, ...data }));
      }
      if (targetId && targetId !== p.cover_id) {
        await api.put(`/properties/${id}/photos`, { order, cover_id: targetId });
      }
      const { data: pres } = await api.post(`/properties/${id}/photos/${targetId || p.cover_id}/presentation`, body);
      setP((prev) => ({ ...prev, ...pres }));
      toast.success("Marketplace cover updated");
    } catch (e) { toast.error(apiError(e)); throw e; }
  };
  const del = async (pid) => { try { const { data } = await api.delete(`/properties/${id}/photos/${pid}`); setP((prev) => ({ ...prev, ...data })); } catch (e) { toast.error(apiError(e)); } };
  const onFiles = async (files) => {
    setUploading(true);
    const fd = new FormData(); Array.from(files).forEach((f) => fd.append("files", f));
    try { const { data } = await api.post(`/properties/${id}/photos`, fd); setP((prev) => ({ ...prev, ...data })); toast.success("Photos added"); }
    catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };

  return (
    <PageShell testid="property-detail-page">
      <button onClick={() => nav(-1)} data-testid="back-btn" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="h-4 w-4" /> Back</button>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-8">
        <div className="min-w-0">
          <ListingGallery photos={photos} alt={p.title} />

          {p.is_owner && (
            <div id="manage-photos-section" className="mt-6 bg-slate-50 border border-border rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="eyebrow">Manage photos · drag to reorder</p>
                <Button data-testid="add-more-photos" onClick={() => fileRef.current?.click()} variant="outline" size="sm" className="rounded-lg" disabled={uploading}>{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ImagePlus className="h-4 w-4 mr-1" /> Add</>}</Button>
                <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => e.target.files.length && onFiles(e.target.files)} />
              </div>
              {(!p.photo_urls || p.photo_urls.length === 0) && <p className="text-sm text-slate-400" data-testid="photos-empty">No photos yet — add some to showcase this listing.</p>}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {(p.photo_urls || []).map((ph, i) => (
                  <div key={ph.id} draggable data-testid={`manage-photo-${i}`}
                    onDragStart={() => setDrag(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
                    className={`relative rounded-lg overflow-hidden border-2 ${p.cover_id === ph.id ? "border-accent" : "border-border"} cursor-grab active:cursor-grabbing`}>
                    <img src={ph.thumb_url || ph.url} alt="" className="h-24 w-full object-cover" loading="lazy" decoding="async" />
                    <GripVertical className="absolute top-1 left-1 h-4 w-4 text-white drop-shadow" />
                    {p.cover_id === ph.id ? (
                      <>
                        <span className="absolute bottom-1 left-1 text-[10px] font-semibold bg-accent text-accent-foreground px-1.5 py-0.5 rounded flex items-center gap-0.5"><Star className="h-2.5 w-2.5" /> Cover</span>
                        <button data-testid="adjust-cover-btn" onClick={() => setComposerOpen(true)} className="absolute top-1 left-1/2 -translate-x-1/2 text-[10px] font-semibold bg-[#0F2C59] text-white px-1.5 py-0.5 rounded flex items-center gap-0.5"><Crop className="h-2.5 w-2.5" /> Cover</button>
                      </>
                    ) : (
                      <button data-testid={`make-cover-${i}`} onClick={() => makeCover(ph.id)} className="absolute bottom-1 left-1 text-[10px] font-semibold bg-white/90 px-1.5 py-0.5 rounded">Set cover</button>
                    )}
                    <button data-testid={`del-photo-${i}`} onClick={() => del(ph.id)} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-[#0A1B33]/60 text-white flex items-center justify-center"><Trash2 className="h-3 w-3" /></button>
                    <select
                      data-testid={`photo-class-${i}`}
                      value={ph.media_class || "PHOTO"}
                      onChange={(e) => setMediaClass(ph.id, e.target.value)}
                      onDragStart={(e) => e.preventDefault()}
                      className="absolute bottom-1 right-1 max-w-[62%] text-[10px] font-semibold bg-white/92 text-[#0F2C59] rounded px-1 py-0.5 border border-[#0F2C59]/15 cursor-pointer"
                      title="Media type"
                    >
                      {PROPERTY_MEDIA_CLASSES.map((c) => <option key={c} value={c}>{MEDIA_CLASS_LABEL[c]}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="min-w-0">
          <span className="text-[11px] font-semibold uppercase tracking-wide bg-secondary text-slate-600 px-2 py-0.5 rounded">{p.listing_type}</span>
          {p.listing_type === "For Sale" && p.sale_type && (
            <span data-testid="property-detail-sale-type" className="ml-1.5 text-[11px] font-semibold uppercase tracking-wide bg-slate-100 text-slate-700 border border-slate-200 px-2 py-0.5 rounded">{saleTypeShortLabel(p.sale_type)}</span>
          )}
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 mt-2">{p.title}</h1>
          <div className="text-slate-500 flex items-center gap-1 mt-1"><MapPin className="h-4 w-4" />{[p.neighborhood, p.city].filter(Boolean).join(", ")}</div>
          <div className="font-display text-3xl font-bold text-slate-900 mt-3">{p.listing_type === "Unit Hosting" ? `₱${(p.nightly_rate || 0).toLocaleString()} / night` : priceLabel(p)}</div>
          {p.listing_type === "Unit Hosting" && <div className="text-sm text-slate-500 mt-1">Up to {p.max_guests} guests · {p.beds} bed(s) · min {p.minimum_stay_nights} night(s) · {(p.availability_status || "AVAILABLE").replace(/_/g, " ").toLowerCase()}{p.check_in_time ? ` · check-in ${p.check_in_time}` : ""}{p.check_out_time ? ` / out ${p.check_out_time}` : ""}{p.house_rules ? ` · ${p.house_rules}` : ""}</div>}

          <ListingCommercialDetails p={p} />

          <AdPlacement placement="PROPERTY_FINANCING" variant="compact" className="mt-4" />

          {p.is_owner && (
            <div className="mt-5 rounded-xl border border-[#0F2C59]/15 bg-white p-3 sm:p-4 shadow-sm" data-testid="owner-toolbar">
              <div className="flex items-center justify-between mb-2.5">
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F2C59]"><ShieldCheck className="h-4 w-4 text-accent" /> You manage this listing</span>
                <span className="text-[11px] font-semibold uppercase tracking-wide bg-secondary text-slate-600 px-2 py-0.5 rounded" data-testid="owner-current-status">{p.status}</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button data-testid="owner-edit-btn" size="sm" onClick={() => nav(`/have-property?edit=${id}`)} className="rounded-lg bg-[#0F2C59] hover:bg-[#0F2C59]/90 text-white font-semibold"><Pencil className="h-4 w-4 mr-1.5" /> Edit Listing</Button>
                <Button data-testid="owner-photos-btn" size="sm" variant="outline" onClick={scrollToPhotos} className="rounded-lg border-[#0F2C59] text-[#0F2C59] font-semibold"><Images className="h-4 w-4 mr-1.5" /> Manage Photos</Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button data-testid="owner-status-trigger" size="sm" variant="outline" disabled={statusBusy} className="rounded-lg border-[#0F2C59] text-[#0F2C59] font-semibold">
                      {statusBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Change Status <ChevronDown className="h-4 w-4 ml-1.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuLabel>Set listing status</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {OWNER_STATUSES.map((s) => (
                      <DropdownMenuItem key={s} data-testid={`owner-status-${s.replace(/\s/g, "-")}`} onClick={() => requestStatus(s)}>Mark {s}</DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button data-testid="owner-share-btn" size="sm" variant="outline" onClick={openShare} className={`rounded-lg font-semibold ${canShare ? "border-[#C79A3B] text-[#8a6a1e] hover:bg-[#C79A3B]/10" : "border-slate-300 text-slate-400"}`}><Share2 className="h-4 w-4 mr-1.5" /> Share Listing</Button>
                <Button data-testid="owner-archive-btn" size="sm" variant="ghost" onClick={requestArchive} className="rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50 font-semibold"><Archive className="h-4 w-4 mr-1.5" /> Archive</Button>
              </div>
              {!canShare && <p className="text-[11px] text-slate-400 mt-2" data-testid="owner-share-hint">{hostingRestricted ? <>Public Stays sharing requires an <span className="font-semibold">active, verified Licensed Broker or Property Manager</span> account.</> : <>Set this listing to <span className="font-semibold">Public Marketplace</span> (Active) to enable public sharing.</>}</p>}
            </div>
          )}

          <div className="grid grid-cols-3 gap-3 mt-5">
            {[["Bedrooms", p.bedrooms], ["Bathrooms", p.bathrooms], ["Area", `${p.floor_area_sqm} sqm`]].map(([k, v]) => (
              <div key={k} className="bg-card border border-border rounded-xl p-3"><div className="text-[11px] text-slate-400">{k}</div><div className="font-semibold text-slate-800">{v}</div></div>
            ))}
          </div>
          {p.features?.length > 0 && (
            <div className="mt-5"><p className="eyebrow mb-2">Features</p><div className="flex flex-wrap gap-2">{p.features.map((f) => <span key={f} className="text-xs bg-secondary text-slate-600 px-2.5 py-1 rounded-full">{f}</span>)}</div></div>
          )}
          {p.description && <p className="text-sm text-slate-600 mt-5 leading-relaxed">{p.description}</p>}

          {p.is_owner && (() => {
            const current = p.visibility || "NETWORK_ONLY";
            return (
              <div className="mt-6 bg-card border border-border rounded-xl p-4" data-testid="visibility-control">
                <p className="eyebrow mb-3">Listing visibility</p>
                <div className="space-y-2">
                  {VISIBILITY_OPTIONS.map((o) => {
                    const active = current === o.key;
                    const restricted = hostingRestricted && o.key === "PUBLIC_MARKETPLACE";
                    return (
                      <button
                        key={o.key}
                        data-testid={`visibility-option-${o.key}`}
                        onClick={() => onSelectVisibility(o.key, current)}
                        disabled={savingVis || restricted}
                        className={`w-full text-left rounded-xl border p-3 flex items-start gap-3 transition-colors ${active ? "border-accent bg-accent/5" : "border-border hover:border-slate-300"} ${restricted ? "opacity-60 cursor-not-allowed" : ""}`}
                      >
                        <o.icon className={`h-5 w-5 mt-0.5 shrink-0 ${active ? "text-accent" : "text-slate-400"}`} />
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">{o.label}{active && <Check className="h-4 w-4 text-accent" data-testid={`visibility-active-${o.key}`} />}</div>
                          <div className="text-xs text-slate-500 mt-0.5" data-testid={restricted ? "visibility-hosting-restricted" : undefined}>{restricted ? "Public Stays listings require an active, verified Licensed Broker or Property Manager account." : o.desc}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {p.is_owner && <Button data-testid="view-buyer-matches" onClick={() => nav(`/property/${id}/matches`)} className="mt-6 w-full bg-accent text-accent-foreground rounded-xl font-semibold">View matching buyers</Button>}
        </div>
      </div>

      <AlertDialog open={pendingVis === "PUBLIC_MARKETPLACE"} onOpenChange={(o) => { if (!o) setPendingVis(null); }}>
        <AlertDialogContent data-testid="visibility-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Publish to Public Marketplace?</AlertDialogTitle>
            <AlertDialogDescription>This property will become visible to anyone browsing PropertyConnect.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="visibility-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="visibility-confirm" onClick={() => applyVisibility("PUBLIC_MARKETPLACE")} className="bg-accent text-accent-foreground hover:bg-accent/90">Publish</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingAction} onOpenChange={(o) => { if (!o) setPendingAction(null); }}>
        <AlertDialogContent data-testid="owner-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{pendingAction?.title}</AlertDialogTitle>
            <AlertDialogDescription>{pendingAction?.body}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="owner-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="owner-confirm-ok" onClick={confirmAction} className={pendingAction?.kind === "archive" ? "bg-red-600 hover:bg-red-700" : "bg-[#0F2C59] hover:bg-[#0F2C59]/90"}>
              {pendingAction?.kind === "archive" ? "Archive Listing" : `Mark ${pendingAction?.status}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {p && (
        <ShareDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          url={listingShareUrl(id)}
          title={`${p.listing_type === "For Rent" ? "For Rent" : "For Sale"} — ${p.title}`}
          text={`${p.title} — ${priceLabel(p)} · ${p.city}. View this property on PropertyConnect.`}
        />
      )}
      {coverPhoto && (
        <CoverComposer open={composerOpen} onOpenChange={setComposerOpen} photos={p.photo_urls || []} coverId={p.cover_id} listing={{ title: p.title, price: p.price, city: p.city, location: p.location }} onCommit={commitCover} />
      )}
    </PageShell>
  );
}
